import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/styles/cn'
import { IClassNameProps } from '@/utils/typings/class-name-props'

type IAdPerformanceScoreBadgeProps = IClassNameProps & {
    /** The raw 0-100 winning-ad score; null for an ad the scorer has not reached yet. */
    score: number | null
    /** Delivery start — ads younger than {@link NEW_AD_SCORE_MIN_DAYS} show "New" instead of a score. */
    startDate?: string | Date | null
}

/**
 * Days an ad must have run before its score is shown. Longevity is half the formula, so a younger
 * ad's low score means "too new to tell", not "bad" — showing it would read as a verdict.
 */
const NEW_AD_SCORE_MIN_DAYS = 7

/**
 * Color band for a 0-100 performance score, aligned with the star-band thresholds
 * (see apps/api/src/services/ads/ad-performance-stars.ts): the 4-5 star range reads
 * positive, the 3 star middle neutral, everything below cautionary.
 */
const scoreClassName = (score: number) => {
    if (score >= 70) return 'border-green-200 bg-green-50 text-green-700'
    if (score >= 50) return 'border-lime-200 bg-lime-50 text-lime-700'
    if (score >= 35) return 'border-amber-200 bg-amber-50 text-amber-700'
    return 'border-red-200 bg-red-50 text-red-700'
}

const isTooNewToScore = (startDate: string | Date | null | undefined) => {
    if (!startDate) return false
    const daysOnline = (Date.now() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)
    return daysOnline >= 0 && daysOnline < NEW_AD_SCORE_MIN_DAYS
}

/**
 * Estimated performance as a color-coded 0-100 badge — the card header's
 * headline metric. Same underlying score as the star bands, just a finer-grained presentation.
 *
 * The slot is never empty: ads too young for a stable estimate show a muted "New", and ads we
 * cannot score yet show a muted "~".
 * Both no-verdict states share the muted chrome so color always means "we have an estimate".
 */
export const AdPerformanceScoreBadge = ({ score, startDate, className }: IAdPerformanceScoreBadgeProps) => {
    if (isTooNewToScore(startDate)) {
        return (
            <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 flex items-center whitespace-nowrap cursor-default font-medium text-muted-foreground',
                            className
                        )}
                    >
                        New
                    </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                    <p className="text-[11px] leading-snug text-muted-foreground">
                        Too new to tell — scores firm up after about a week of running.
                    </p>
                </TooltipContent>
            </Tooltip>
        )
    }

    if (score === null) {
        return (
            <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                    <Badge
                        variant="outline"
                        className={cn(
                            'h-5 flex items-center whitespace-nowrap cursor-default font-medium text-muted-foreground',
                            className
                        )}
                    >
                        ~
                    </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                    <p className="text-[11px] leading-snug text-muted-foreground">
                        Meta doesn&apos;t share enough data to compute a fair score for this ad.
                    </p>
                </TooltipContent>
            </Tooltip>
        )
    }

    const display = String(score)

    return (
        <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
                <Badge
                    variant="outline"
                    className={cn(
                        'h-5 flex items-center whitespace-nowrap cursor-default font-semibold tabular-nums',
                        scoreClassName(score),
                        className
                    )}
                >
                    {display}
                </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">
                <div className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="text-xs font-semibold">Estimated performance</span>
                        <span className="text-xs font-semibold">{display} / 100</span>
                    </div>
                    <p className="text-[11px] leading-snug text-muted-foreground">
                        This score is computed based on advertiser behavior: successful ads run longer, get more
                        variations, and earn more reach. Each ad is scored relative to the advertiser&apos;s other ads,
                        with 70+ marking their strongest.
                    </p>
                </div>
            </TooltipContent>
        </Tooltip>
    )
}
